import { useEffect, useMemo, useState } from "react";
import { useLocation, useHistory, useRouteMatch } from "react-router-dom";
import {
  Button,
  Flex,
  FileObjectType,
  FileUpload,
  ModalV2,
  Text,
  Banner,
  toast,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { type S3DocumentInput } from "~/graphql/mainTypes";
import {
  useCreateDocumentsMutation,
  useCreateDocumentsByArenaMutation,
} from "~/graphql/supergraphHooks";
import useComplianceUploadFilesToS3 from "~/hooks/uploadFilesToComplianceS3/useComplianceUploadFilesToS3";
import { getErrorMessage } from "~/utils/errors";
import {
  EXTRACTION_FAILED_ERROR,
  FILE_TYPE_NOT_SUPPORTED_ERROR,
  UNSUPPORTED_DOC_TYPE_ERROR,
  getBannerTitle,
  getFileCardError,
} from "../../../../Documents/UploadModal/utils";
import { ProfileCreationModalFooter } from "./ProfileCreationModalFooter";
import {
  ExtractionStatus,
  useDocumentExtractionPolling,
} from "./ProfileCreationModalFooter/useDocumentExtractionPolling";
import * as styles from "./styles_vanilla.css";

export const ProfileCreationModal = ({
  arenaId,
  onManualCreate,
  onExtractionSuccess,
  onClose,
}: {
  arenaId?: number | null;
  onManualCreate?: () => void;
  onExtractionSuccess?: (documentId: number) => void;
  onClose?: () => void;
}) => {
  const history = useHistory();
  const location = useLocation();
  const caseMatch = useRouteMatch<{ caseId: string }>("/compliance/case/:caseId");
  const caseId = caseMatch?.params.caseId ? parseInt(caseMatch.params.caseId, 10) : undefined;
  const [selectedFiles, setSelectedFiles] = useState<FileObjectType[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [uploadedDocumentId, setUploadedDocumentId] = useState<number | undefined>();

  const { extractionStatus } = useDocumentExtractionPolling({
    documentId: uploadedDocumentId,
  });

  useEffect(() => {
    if (extractionStatus === ExtractionStatus.Succeeded && uploadedDocumentId) {
      if (onExtractionSuccess) {
        onExtractionSuccess(uploadedDocumentId);
        return;
      }
      const searchParams = new URLSearchParams(location.search);
      searchParams.append("documentId", String(uploadedDocumentId));
      history.push({
        pathname: `${location.pathname}/preview`,
        search: searchParams.toString(),
        state: location.state,
      });
    }

    if (extractionStatus === ExtractionStatus.UnsupportedDocType) {
      setErrorMessage(UNSUPPORTED_DOC_TYPE_ERROR);
    }

    if (extractionStatus === ExtractionStatus.Failed) {
      setErrorMessage(EXTRACTION_FAILED_ERROR);
    }
  }, [
    extractionStatus,
    uploadedDocumentId,
    history,
    location.pathname,
    location.search,
    location.state,
    onExtractionSuccess,
  ]);

  const { uploadFilesToS3 } = useComplianceUploadFilesToS3();
  const [createDocuments] = useCreateDocumentsMutation();
  const [createDocumentsWithoutCase] = useCreateDocumentsByArenaMutation();

  const serverRejectedFiles = useMemo(() => {
    const fileCardError = getFileCardError(errorMessage);
    if (!fileCardError || selectedFiles.length === 0) return undefined;
    return selectedFiles.map((f) => ({ file: f.file, error: fileCardError }));
  }, [errorMessage, selectedFiles]);

  const handleFilesSelected = (files: FileObjectType[]) => {
    const acceptedFiles = files.filter((file) => file.status === "accepted");
    const hasRejectedFiles = files.some((file) => file.status === "rejected");
    setSelectedFiles(acceptedFiles);
    setErrorMessage(hasRejectedFiles ? FILE_TYPE_NOT_SUPPORTED_ERROR : null);
  };

  const handleUploadAndExtract = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    setErrorMessage(null);

    try {
      const uploadResponse = await uploadFilesToS3(selectedFiles.map((x) => x.file));
      const s3ObjectKeys = uploadResponse.map((s3File): S3DocumentInput => {
        return {
          objectKey: s3File.objectKey,
          name: s3File.file.name,
          size: s3File.file.size,
          type: s3File.file.type,
        };
      });

      const documentInputs = s3ObjectKeys.map((s3ObjectKey) => ({
        contentType: s3ObjectKey.type,
        fileName: s3ObjectKey.name || "",
        s3Key: s3ObjectKey.objectKey || "",
      }));

      let documentIds: number[] | undefined;
      let createdDocumentsResult;

      if (caseId) {
        createdDocumentsResult = await createDocuments({
          variables: { caseId, documents: documentInputs, processExtraction: true },
        });
        documentIds = createdDocumentsResult.data?.createComplianceDocuments.map(
          (doc) => doc.id,
        );
      } else {
        createdDocumentsResult = await createDocumentsWithoutCase({
          variables: { arenaId: arenaId ?? 0, documents: documentInputs },
        });
        documentIds = createdDocumentsResult.data?.createOnboardingDocumentsWithoutCase.map(
          (doc) => doc.id,
        );
      }

      if (!createdDocumentsResult.errors && documentIds && documentIds[0]) {
        setUploadedDocumentId(documentIds[0]);
      } else {
        setErrorMessage(EXTRACTION_FAILED_ERROR);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsUploading(false);
    }
  };

  const closeModal = () => {
    if (onClose) {
      onClose();
      return;
    }
    history.push({
      pathname: `${location.pathname.split("/extraction")[0]}`,
      search: location.search,
    });
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setIsUploading(false);
    setErrorMessage(null);
    closeModal();
  };

  const handleManualCreate = () => {
    if (onManualCreate) {
      onManualCreate();
    } else {
      closeModal();
    }
  };

  return (
    <ModalV2 show={true} onHide={handleClose} title="Select file">
      <ModalV2.Body>
        <Flex flexDirection="column">
          <Text variant="regular-md" className={styles.descriptionText}>
            Upload a file containing the information for an investor account profile.
            <br />
            (e.g., Driver&apos;s License, Passport, Certificate of Formation, Articles of
            Incorporation)
          </Text>

          {errorMessage &&
            (errorMessage === EXTRACTION_FAILED_ERROR ? (
              <div className={styles.inlineErrorBanner}>
                <Icon type="exclamation-circle" color={Colors.Danger600} variant="solid" />
                <Text asSpan>
                  Please upload a different document or{" "}
                  <Button displayAsLink isInline onClick={handleManualCreate}>
                    manually create a profile
                  </Button>
                  .
                </Text>
              </div>
            ) : (
              <Banner
                variant="danger"
                title={getBannerTitle(errorMessage)}
                className={styles.errorBanner}
              />
            ))}

          <div className={styles.fileUploadWrapper}>
            <FileUpload
              onFileSelected={handleFilesSelected}
              acceptedFileTypes={[".jpg", ".jpeg", ".png", ".pdf"]}
              isFullWidth
              uploadMessage="Drop the file here or"
              serverRejectedFiles={serverRejectedFiles}
            />
          </div>

          {selectedFiles.length > 0 && !errorMessage && (
            <ProfileCreationModalFooter
              isUploading={isUploading}
              onUploadWithAI={handleUploadAndExtract}
              onDocumentUploadSuccess={uploadedDocumentId !== undefined}
              extractionStatus={extractionStatus}
            />
          )}
        </Flex>
      </ModalV2.Body>
    </ModalV2>
  );
};
